import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Search, Menu, FolderOpen, LayoutDashboard, FileText, ArrowLeftRight } from 'lucide-react';
import { pmT, fill } from '../../lib/i18nPm';
import { BRANCHES, BR_ICON, openTasks, pmOf } from '../../lib/pm';
import './pm.css';
import { pkgCount } from '../../lib/pmPkg';
import { archiveSupported } from '../../lib/archive';
import { BrChip } from './BrChip';
import { SubChip } from './SubChip';
import { PmOverview } from './PmOverview';
import { PmReports } from './PmReports';
import { PmHandover } from './PmHandover';
import { PmMenu } from './PmMenu';
import { PmNew } from './PmNew';

const brOf = (p) => pmOf(p).branches;
const hit = (p, q) => !q || [p.title, p.address, p.pm?.customer].some((x) => String(x || '').toLowerCase().includes(q.toLowerCase()));

function Status({ p }) {
  const T = pmT(), o = openTasks(p), [n, setN] = useState(0);
  useEffect(() => { pkgCount(p).then((c) => setN(c.reports)).catch(() => setN(0)); }, [p]);
  return (
    <div className="pm-status" data-testid="pm-status">
      <span className="pm-badge ok">{T.saved}</span>
      <span className="pm-badge">{p.pm?.rev ? fill(T.rev, { n: p.pm.rev }) : T.revNone}</span>
      {n > 0 && <span className="pm-badge warn" data-testid="pm-status-ready">{fill(T.ready, { n })}</span>}
      {o.drafts + o.inbox > 0 && <span className="pm-badge bad" data-testid="pm-status-check">{fill(T.check, { n: o.drafts + o.inbox })}</span>}
      {o.conflicts > 0 && <span className="pm-badge bad" data-testid="pm-status-conf">{fill(T.conf, { n: o.conflicts })}</span>}
    </div>
  );
}

// desktop / large tablet: project list left, three views right; entry stays the archive button
export default function ProjectManager({ t, app, onClose, onFolder, initPkg }) {
  const T = pmT(), [q, setQ] = useState(''), [br, setBr] = useState(''), [tab, setTab] = useState(initPkg ? 'ho' : 'ov'), [menu, setMenu] = useState(false), [nw, setNw] = useState(false);
  const curId = app.current?.id;
  const list = useMemo(() => app.projects.filter((p) => !p.__fresh || p.id === curId).filter((p) => (!br || brOf(p).includes(br)) && hit(p, q)).sort((a, b) => b.updatedAt - a.updatedAt), [app.projects, curId, br, q]);
  const [sel, setSel] = useState(initPkg?.projectId && app.projects.some((x) => x.id === initPkg.projectId) ? initPkg.projectId : app.current?.id);
  const p = list.find((x) => x.id === sel) || list[0];
  const V = { ov: PmOverview, rep: PmReports, ho: PmHandover }[tab];
  return createPortal(
    <div className="pm-overlay" data-testid="pm-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="pm-window">
        <aside className="pm-sidebar">
          <div className="pm-side-head"><b>🗄️ {T.title}</b><button className="pm-btn pm-primary" data-testid="pm-new-btn" onClick={() => setNw(true)}><Plus size={15} /> {T.newProj}</button></div>
          <div className="pm-search-box">
            <label className="pm-input-ico"><Search size={14} /><input className="pm-input" data-testid="pm-search" placeholder={T.search} value={q} onChange={(e) => setQ(e.target.value)} /></label>
            <div className="pm-branch-chips">
              <button className={`pm-chip ${!br ? 'active' : ''}`} data-testid="pm-filter-all" onClick={() => setBr('')}>{T.allBr}</button>
              {BRANCHES.map((b) => <BrChip key={b} b={b} t={t} active={br === b} onClick={() => setBr(br === b ? '' : b)} testid={`pm-filter-${b}`} />)}
            </div>
          </div>
          <ul className="pm-project-list" data-testid="pm-project-list">
            {!list.length && <li className="pm-empty">{T.none}</li>}
            {list.map((x) => (
              <li key={x.id}><button className={`pm-project-card ${p?.id === x.id ? 'active' : ''}`} data-testid={`pm-proj-${x.id}`} onClick={() => setSel(x.id)}>
                <b>{x.title}</b>
                <small>{x.mode === 'single' ? T.singleRep : pmOf(x).kind === 'cross' ? T.kCross : T.kSingle} · {x.reports.length} {x.reports.length === 1 ? T.rep1 : T.tRep}</small>
                <span className="pm-mini-br">{brOf(x).map((b) => <i key={b} title={t[`pf_${b}`] || b}>{BR_ICON[b]}</i>)}</span>
              </button></li>
            ))}
          </ul>
          {!app.projects.some((x) => x.pm?.demo) && <button className="pm-btn pm-ghost pm-folder" data-testid="pm-demo-btn" onClick={async () => { await app.demo(); }}>✨ {T.demo}</button>}
          {archiveSupported() && <button className="pm-btn pm-ghost pm-folder" data-testid="pm-folder-archive" onClick={onFolder}><FolderOpen size={15} /> {T.folderArchive}</button>}
        </aside>
        <section className="pm-main">
          <header className="pm-head">
            <div>
              <h2 data-testid="pm-title">{p?.title || '–'}</h2>
              {p && <div className="pm-head-br">{brOf(p).map((b) => <BrChip key={b} b={b} t={t} />)}{brOf(p).includes('detect') && (p.pm?.subs || []).map((s) => <SubChip key={s} s={s} t={t} testid={`pm-head-sub-${s}`} />)}</div>}
            </div>
            <div className="pm-head-act">
              {p && <button className="pm-btn pm-ghost" data-testid="pm-menu-btn" onClick={() => setMenu(true)}><Menu size={16} /> {T.menu}</button>}
              <button className="pm-icon" data-testid="pm-close-btn" onClick={onClose} aria-label={T.close}><X size={18} /></button>
            </div>
          </header>
          {p && <Status p={p} />}
          <nav className="pm-tabs">
            {[['ov', T.tOv, LayoutDashboard], ['rep', T.tRep, FileText], ['ho', T.tHo, ArrowLeftRight]].map(([k, l, I]) => <button key={k} className={`pm-tab ${tab === k ? 'active' : ''}`} data-testid={`pm-tab-${k}`} onClick={() => setTab(k)}><I size={15} /> {l}</button>)}
          </nav>
          <div className="pm-body">{p ? <V t={t} p={p} app={app} initPkg={initPkg} /> : <p className="pm-empty">{T.none}</p>}</div>
        </section>
      </div>
      {menu && p && <PmMenu t={t} p={p} app={app} onClose={() => setMenu(false)} />}
      {nw && <PmNew t={t} app={app} onClose={() => setNw(false)} onDone={(id) => { setNw(false); setSel(id); setTab('ov'); }} />}
    </div>,
    document.body,
  );
}
